import { useTimeoutFn } from '@vueuse/core';

const TOOLTIP_DISMISSED_KEY = 'wabunka-ai-chat-tooltip-dismissed';

const TOOLTIP_DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // dismiss の有効期限（経過後は再び表示される）。誤クリック救済と長期離脱者の再認知用。
const TOOLTIP_FALLBACK_DELAY_MS = 30_000; // overview を読まずに留まるユーザーにも気付かせるためのフォールバック遅延
const TOOLTIP_AUTO_HIDE_MS = 10_000; // 表示開始から自動で消すまでの時間（永続化しない＝同セッション内でのみ非表示）

interface Options {
  isOpen: MaybeRefOrGetter<boolean>;
  hasOverviewBeenSeen: MaybeRefOrGetter<boolean>;
}

/**
 * ChatFAB の「Ask Wabunka AI Chat」吹き出しツールチップの表示状態と
 * ライフサイクル（フォールバック表示 / 自動消失 / dismiss 永続化）を管理する composable。
 *
 * 表示条件: 未 dismiss && 自動消失していない && チャット未オープン && (overview 通過済み OR フォールバック遅延経過)
 *          ※dismiss は localStorage に timestamp で永続化し、TTL（7日）経過後に再表示される。
 */
export const useChatTooltipState = (options: Options) => {
  const isDismissed = ref(false); // ツールチップを既に閉じたか
  const isFallbackDelayElapsed = ref(false); // フォールバック遅延を経過したか
  const isAutoHidden = ref(false); // 自動非表示になったか

  // dismiss 済みかどうか
  const readPersistedDismiss = (): boolean => {
    const raw = localStorage.getItem(TOOLTIP_DISMISSED_KEY);
    if (!raw) return false;
    const ts = Number(raw);

    // 不正値（NaN / Infinity）または TTL 経過なら期限切れ
    if (!Number.isFinite(ts) || Date.now() - ts >= TOOLTIP_DISMISS_TTL_MS) {
      localStorage.removeItem(TOOLTIP_DISMISSED_KEY);
      return false;
    }
    return true;
  };

  const dismiss = () => {
    isDismissed.value = true;
    localStorage.setItem(TOOLTIP_DISMISSED_KEY, String(Date.now()));
  };

  // マウントから一定時間でツールチップを強制表示するフォールバック（dismiss 済みなら起動しない）
  const { start: startFallback } = useTimeoutFn(
    () => {
      isFallbackDelayElapsed.value = true;
    },
    TOOLTIP_FALLBACK_DELAY_MS,
    { immediate: false },
  );

  // ツールチップ表示開始から TOOLTIP_AUTO_HIDE_MS 後に自動非表示
  const { start: startAutoHide } = useTimeoutFn(
    () => {
      isAutoHidden.value = true;
    },
    TOOLTIP_AUTO_HIDE_MS,
    { immediate: false },
  );

  onMounted(() => {
    isDismissed.value = readPersistedDismiss();
    if (!isDismissed.value) startFallback();
  });

  // ツールチップの表示条件を満たすかどうか
  const isVisible = computed(() => {
    if (isDismissed.value) return false;
    if (isAutoHidden.value) return false;
    if (toValue(options.isOpen)) return false;
    return toValue(options.hasOverviewBeenSeen) || isFallbackDelayElapsed.value;
  });

  // ツールチップが表示されたタイミングから、TOOLTIP_AUTO_HIDE_MS 後に非表示にするタイマーを起動
  watch(
    isVisible,
    (visible) => {
      if (visible) startAutoHide();
    },
    { once: true },
  );

  return { isVisible, dismiss };
};
